import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { customerController, calculateEMI, generateSchedule } from '../../controllers/customerController';
import {
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle,
  ChevronUp, ChevronDown, CheckCircle, User, FileText, Calendar,
  TrendingUp, CheckSquare, AlertTriangle, Upload, ChevronRight,
  ChevronLeft, RotateCcw, Save, IndianRupee, XCircle, Pen,
  Smartphone, RefreshCw,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:    { padding: 0, fontFamily: "'DM Sans', sans-serif" },
  card:    { background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflow: 'hidden' },
  modal:   { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  mBox:    { background: '#fff', borderRadius: 16, width: '100%', maxHeight: '94vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.25)' },
  mHead:   { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  mFoot:   { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, background: '#fafafa' },
  mBody:   { padding: 24, overflowY: 'auto', flex: 1 },
  mTitle:  { fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 },
  btnPri:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background .15s' },
  btnSec:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnDng:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGrn:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  iconBtn: { padding: 6, background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  label:   { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.04em' },
  input:   { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', color: '#1e293b', background: '#fff', boxSizing: 'border-box', transition: 'border .15s' },
  th:      { background: '#f8fafc', padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.05em' },
  td:      { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  badge:   (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg }),
  section: { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 14, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 },
  divider: { height: 1, background: '#f1f5f9', margin: '20px 0' },
  errBox:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16 },
  infoBox: (color) => ({ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: 10, padding: '14px 16px', marginBottom: 16 }),
  grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
  progBar: (pct, color) => ({
    outer: { height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginTop: 4 },
    inner: { height: '100%', width: `${Math.min(100, Math.max(0, pct))}%`, background: color, borderRadius: 3, transition: 'width .4s' },
  }),
  fileUp:  { border: '2px dashed #d1d5db', borderRadius: 10, padding: '18px 12px', textAlign: 'center', cursor: 'pointer', transition: 'border .15s, background .15s' },
  tab:     (active) => ({ padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: active ? 700 : 500, cursor: 'pointer', border: 'none', background: active ? '#2563eb' : 'transparent', color: active ? '#fff' : '#64748b', transition: 'all .15s' }),
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const ErrList = ({ errors }) =>
  errors?.length ? (
    <div style={S.errBox}>
      {errors.map((e, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626', fontSize: 13, marginBottom: i < errors.length - 1 ? 4 : 0 }}>
          <AlertCircle size={13} /> {e}
        </div>
      ))}
    </div>
  ) : null;

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const paymentStatusColor = (s) =>
  ({ Paid: { color: '#16a34a', bg: '#dcfce7' }, Pending: { color: '#2563eb', bg: '#dbeafe' }, Overdue: { color: '#dc2626', bg: '#fee2e2' }, Partial: { color: '#d97706', bg: '#fef3c7' } }[s] || { color: '#64748b', bg: '#f3f4f6' });

// ─────────────────────────────────────────────────────────────────────────────
// SIGNATURE PAD
// ─────────────────────────────────────────────────────────────────────────────
const SignaturePad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSign, setHasSign] = useState(!!value);

  const getPos = (e, cvs) => {
    const rect = cvs.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (cvs.width / rect.width), y: (src.clientY - rect.top) * (cvs.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e, canvasRef.current); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw  = (e) => { e.preventDefault(); if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e, canvasRef.current); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.stroke(); };
  const stop  = () => { if (!drawing.current) return; drawing.current = false; const data = canvasRef.current.toDataURL(); setHasSign(true); onChange(data); };
  const clear = () => { canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); setHasSign(false); onChange(null); };

  useEffect(() => {
    if (value && canvasRef.current && !hasSign) {
      const img = new window.Image();
      img.onload = () => canvasRef.current?.getContext('2d').drawImage(img, 0, 0);
      img.src = value;
    }
  }, []); // eslint-disable-line

  return (
    <div>
      <div style={{ border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', background: '#fafafa' }}>
        <canvas ref={canvasRef} width={500} height={130}
          style={{ display: 'block', width: '100%', touchAction: 'none', cursor: 'crosshair' }}
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
        <button type="button" style={S.btnSec} onClick={clear}><RotateCcw size={13} /> Clear</button>
        {hasSign
          ? <span style={{ fontSize: 12, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={13} /> Signature captured</span>
          : <span style={{ fontSize: 12, color: '#94a3b8' }}>Draw your signature above</span>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FILE UPLOAD FIELD
// ─────────────────────────────────────────────────────────────────────────────
const FileField = ({ label, value, onChange, accept = 'image/*' }) => {
  const ref = useRef();
  const isImg = value && (value.startsWith('data:image') || value.startsWith('http'));
  return (
    <div>
      <label style={S.label}>{label}</label>
      <div
        style={{ ...S.fileUp, borderColor: value ? '#2563eb' : '#d1d5db', background: value ? '#eff6ff' : '#fff' }}
        onClick={() => ref.current.click()}>
        {isImg
          ? <img src={value} alt={label} style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 6, objectFit: 'contain' }} />
          : value
            ? <div style={{ fontSize: 13, color: '#2563eb', fontWeight: 600 }}>✓ {label} uploaded</div>
            : <div style={{ fontSize: 13, color: '#94a3b8' }}><Upload size={16} style={{ marginBottom: 4 }} /><br />Click to upload {label}</div>}
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files[0];
            if (!f) return;
            const reader = new FileReader();
            reader.onload = () => onChange(reader.result, f);
            reader.readAsDataURL(f);
          }} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EMI SECTION — uses customerController's generateSchedule
// ─────────────────────────────────────────────────────────────────────────────
const defaultEmiPlan = () => ({
  productName: '',
  productPrice: 0,
  downPayment: 0,
  interestRate: 0,
  tenure: 12,
  tenureType: 'monthly',
  customDays: 30,
  startDate: new Date().toISOString().split('T')[0],
  emiAmount: 0,
  totalPayable: 0,
  totalInterest: 0,
  payments: [],
  status: 'Active',
});

const EMISection = ({ emiPlan, onChange }) => {
  const [plan, setPlan] = useState(() => emiPlan ? { ...defaultEmiPlan(), ...emiPlan } : defaultEmiPlan());
  const [calculated, setCalculated] = useState(!!(emiPlan?.emiAmount > 0));
  const [calcError, setCalcError] = useState('');

  const update = (field, val) => {
    const updated = { ...plan, [field]: val };
    setPlan(updated);
    setCalculated(false);
    onChange(updated);
  };

  const handleCalculate = () => {
    setCalcError('');
    if (!plan.productPrice || plan.productPrice <= 0) { setCalcError('Product price is required'); return; }
    if (plan.downPayment >= plan.productPrice) { setCalcError('Down payment must be less than product price'); return; }
    if (!plan.tenure || plan.tenure < 1) { setCalcError('Tenure must be at least 1'); return; }

    try {
      // calls the same generateSchedule from customerController
      const result = generateSchedule(plan);
      const updated = { ...plan, ...result };
      setPlan(updated);
      setCalculated(true);
      onChange(updated);
    } catch (e) {
      setCalcError(e.message || 'Calculation failed');
    }
  };

  const tenureLabel = { weekly: 'Weeks', monthly: 'Months', yearly: 'Years', custom: 'Periods' }[plan.tenureType] || 'Months';

  return (
    <div>
      {calcError && (
        <div style={{ ...S.errBox, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626', fontSize: 13 }}>
            <AlertCircle size={13} /> {calcError}
          </div>
        </div>
      )}

      <div style={S.grid2}>
        <div>
          <label style={S.label}>Product / Item Name</label>
          <input style={S.input} value={plan.productName} onChange={e => update('productName', e.target.value)} placeholder="e.g. Samsung Galaxy S24" />
        </div>
        <div>
          <label style={S.label}>Product Price (₹) *</label>
          <input type="number" style={S.input} value={plan.productPrice || ''} onChange={e => update('productPrice', +e.target.value)} placeholder="0" min="0" />
        </div>
        <div>
          <label style={S.label}>Down Payment (₹)</label>
          <input type="number" style={S.input} value={plan.downPayment || ''} onChange={e => update('downPayment', +e.target.value)} placeholder="0" min="0" />
        </div>
        <div>
          <label style={S.label}>Interest Rate (% per annum)</label>
          <input type="number" style={S.input} value={plan.interestRate || ''} onChange={e => update('interestRate', +e.target.value)} placeholder="0" step="0.1" min="0" />
        </div>
        <div>
          <label style={S.label}>Tenure Type</label>
          <select style={S.input} value={plan.tenureType} onChange={e => update('tenureType', e.target.value)}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="custom">Custom Days</option>
          </select>
        </div>
        <div>
          <label style={S.label}>Tenure ({tenureLabel}) *</label>
          <input type="number" style={S.input} value={plan.tenure || ''} onChange={e => update('tenure', +e.target.value)} min="1" placeholder="12" />
        </div>
        {plan.tenureType === 'custom' && (
          <div>
            <label style={S.label}>Custom Interval (Days)</label>
            <input type="number" style={S.input} value={plan.customDays || ''} onChange={e => update('customDays', +e.target.value)} min="1" placeholder="30" />
          </div>
        )}
        <div>
          <label style={S.label}>Start Date</label>
          <input type="date" style={S.input} value={plan.startDate} onChange={e => update('startDate', e.target.value)} />
        </div>
      </div>

      <button
        type="button"
        style={{ ...S.btnPri, marginTop: 14, width: '100%', justifyContent: 'center' }}
        onClick={handleCalculate}>
        <TrendingUp size={15} /> Calculate EMI & Generate Schedule
      </button>

      {calculated && plan.emiAmount > 0 && (
        <div style={{ marginTop: 16 }}>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
            {[
              ['EMI Amount',     fmt(plan.emiAmount),     '#2563eb', '#dbeafe'],
              ['Total Payable',  fmt(plan.totalPayable),  '#0f172a', '#f1f5f9'],
              ['Total Interest', fmt(plan.totalInterest), '#dc2626', '#fee2e2'],
              ['Down Payment',   fmt(plan.downPayment),   '#16a34a', '#dcfce7'],
            ].map(([lbl, val, color, bg]) => (
              <div key={lbl} style={{ background: bg, borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Schedule table */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['#', 'Due Date', 'EMI', 'Principal', 'Interest', 'Balance', 'Status'].map(h => (
                    <th key={h} style={{ background: '#f8fafc', padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(plan.payments || []).map((p, i) => {
                  const sc = paymentStatusColor(p.status);
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>{p.installmentNo}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{p.dueDate}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>{fmt(p.emiAmount)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#16a34a' }}>{fmt(p.principal)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#dc2626' }}>{fmt(p.interest)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>{fmt(p.balance)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={S.badge(sc.color, sc.bg)}>{p.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAYMENT HISTORY MODAL
// ─────────────────────────────────────────────────────────────────────────────
const PaymentHistoryModal = ({ customer, onClose, onUpdate }) => {
  const [payments, setPayments] = useState(customer?.emiPlan?.payments || []);
  const [updating, setUpdating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payForm, setPayForm] = useState({
    paidAmount: '',
    paidDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const plan = customer?.emiPlan;
  if (!plan) return null;

  const totalPaid = payments.reduce((s, p) => s + (p.paidAmount || 0), 0);
  const totalPayable = plan.totalPayable || 0;
  const progress = totalPayable ? Math.round((totalPaid / totalPayable) * 100) : 0;

  const markPayment = async (idx) => {
    setSaving(true);
    const p = payments[idx];
    const paid = +payForm.paidAmount || p.emiAmount;
    const paymentData = {
      installmentNo: p.installmentNo,
      paidAmount: paid,
      paidDate: payForm.paidDate,
      notes: payForm.notes,
      status: paid >= p.emiAmount ? 'Paid' : 'Partial',
    };
    // Call actual controller
    const r = await customerController.updatePayment(customer._id || customer.id, paymentData);
    if (r.success) {
      const updated = payments.map((pm, i) => i === idx ? { ...pm, ...paymentData } : pm);
      setPayments(updated);
      setUpdating(null);
      onUpdate(updated);
    }
    setSaving(false);
  };

  const markOverdue = async (idx) => {
    const p = payments[idx];
    const r = await customerController.markOverdue(customer._id || customer.id, p.installmentNo);
    if (r.success) {
      setPayments(prev => prev.map((pm, i) => i === idx ? { ...pm, status: 'Overdue' } : pm));
    }
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mBox, maxWidth: 920 }} onClick={e => e.stopPropagation()}>
        <div style={S.mHead}>
          <div>
            <h2 style={S.mTitle}>Payment History — {customer.fullName}</h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {customer.customerKey} · {plan.productName} · {fmt(plan.productPrice)}
            </div>
          </div>
          <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div style={S.mBody}>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 20 }}>
            {[
              ['Total Payable', fmt(totalPayable),           '#0f172a', '#f1f5f9'],
              ['Total Paid',    fmt(totalPaid),              '#16a34a', '#dcfce7'],
              ['Remaining',     fmt(totalPayable - totalPaid),'#2563eb', '#dbeafe'],
              ['EMI Amount',    fmt(plan.emiAmount),         '#7c3aed', '#ede9fe'],
              ['Progress',      `${progress}%`,              '#ea580c', '#ffedd5'],
            ].map(([lbl, val, color, bg]) => (
              <div key={lbl} style={{ background: bg, borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={S.progBar(progress, '#2563eb').outer}>
            <div style={S.progBar(progress, '#2563eb').inner} />
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 20, marginTop: 4 }}>
            {payments.filter(p => p.status === 'Paid').length} of {payments.length} installments paid
          </div>

          {/* Table */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['#', 'Due Date', 'EMI', 'Paid Amount', 'Paid Date', 'Status', 'Notes', 'Action'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => {
                  const sc = paymentStatusColor(p.status);
                  const isUpd = updating === i;
                  return (
                    <Fragment key={i}>
                      <tr style={{ background: p.status === 'Paid' ? '#f0fdf4' : p.status === 'Overdue' ? '#fff5f5' : '#fff' }}>
                        <td style={S.td}>{p.installmentNo}</td>
                        <td style={S.td}>{typeof p.dueDate === 'string' ? p.dueDate : new Date(p.dueDate).toISOString().split('T')[0]}</td>
                        <td style={{ ...S.td, fontWeight: 700 }}>{fmt(p.emiAmount)}</td>
                        <td style={{ ...S.td, color: p.paidAmount ? '#16a34a' : '#94a3b8' }}>
                          {p.paidAmount ? fmt(p.paidAmount) : '—'}
                        </td>
                        <td style={{ ...S.td, fontSize: 12, color: '#64748b' }}>
                          {p.paidDate ? (typeof p.paidDate === 'string' ? p.paidDate : new Date(p.paidDate).toISOString().split('T')[0]) : '—'}
                        </td>
                        <td style={S.td}><span style={S.badge(sc.color, sc.bg)}>{p.status}</span></td>
                        <td style={{ ...S.td, fontSize: 12, color: '#64748b', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.notes || '—'}</td>
                        <td style={S.td}>
                          {p.status !== 'Paid' ? (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button
                                style={{ ...S.btnGrn, padding: '5px 10px', fontSize: 11 }}
                                onClick={() => setUpdating(isUpd ? null : i)}>
                                <CheckSquare size={12} /> Mark Paid
                              </button>
                              {p.status !== 'Overdue' && (
                                <button
                                  style={{ ...S.btnDng, padding: '5px 10px', fontSize: 11 }}
                                  onClick={() => markOverdue(i)}>
                                  <AlertTriangle size={12} />
                                </button>
                              )}
                            </div>
                          ) : <span style={{ color: '#16a34a', fontSize: 12 }}>✓ Done</span>}
                        </td>
                      </tr>
                      {isUpd && (
                        <tr style={{ background: '#f0f9ff' }}>
                          <td colSpan={8} style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                              <div>
                                <label style={S.label}>Paid Amount (₹)</label>
                                <input type="number" style={{ ...S.input, width: 140 }}
                                  value={payForm.paidAmount} placeholder={String(p.emiAmount)}
                                  onChange={e => setPayForm(f => ({ ...f, paidAmount: e.target.value }))} />
                              </div>
                              <div>
                                <label style={S.label}>Payment Date</label>
                                <input type="date" style={{ ...S.input, width: 160 }}
                                  value={payForm.paidDate}
                                  onChange={e => setPayForm(f => ({ ...f, paidDate: e.target.value }))} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={S.label}>Notes</label>
                                <input style={S.input} value={payForm.notes} placeholder="Payment notes..."
                                  onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
                              </div>
                              <button style={S.btnGrn} disabled={saving} onClick={() => markPayment(i)}>
                                <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button style={S.btnSec} onClick={() => setUpdating(null)}><X size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={S.mFoot}>
          <button style={S.btnSec} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FCM TOKEN MODAL
// ─────────────────────────────────────────────────────────────────────────────
const FCMTokenModal = ({ customer, onClose, onUpdated }) => {
  const [token, setToken] = useState(customer?.fcmToken || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!token.trim()) { setError('FCM token is required'); return; }
    setError('');
    setSaving(true);
    // update via customerController.update — patch only fcmToken field
    const r = await customerController.update(customer._id || customer.id, { fcmToken: token.trim() });
    if (r.success) {
      onUpdated(token.trim());
      onClose();
    } else {
      setError(r.message || 'Failed to update FCM token');
    }
    setSaving(false);
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mBox, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div style={S.mHead}>
          <h2 style={S.mTitle}>Update FCM Token</h2>
          <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>
        </div>
        <div style={S.mBody}>
          <div style={{ ...S.infoBox('#2563eb'), fontSize: 13 }}>
            <Smartphone size={14} style={{ display: 'inline', marginRight: 6 }} />
            FCM (Firebase Cloud Messaging) token is used to send push notifications to this customer's device.
          </div>
          {error && (
            <div style={{ ...S.errBox, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626', fontSize: 13 }}>
                <AlertCircle size={13} /> {error}
              </div>
            </div>
          )}
          <div>
            <label style={S.label}>Customer</label>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 16 }}>
              {customer.fullName} · {customer.phoneNumber}
              {customer.customerKey && <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#64748b', marginLeft: 8 }}>{customer.customerKey}</span>}
            </div>
          </div>
          <div>
            <label style={S.label}>FCM Token *</label>
            <textarea
              style={{ ...S.input, minHeight: 90, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder="Paste Firebase FCM token here..."
            />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              Token is usually 150–200 characters long
            </div>
          </div>
        </div>
        <div style={S.mFoot}>
          <button style={S.btnSec} onClick={onClose}>Cancel</button>
          <button style={S.btnPri} onClick={handleSave} disabled={saving}>
            <RefreshCw size={14} /> {saving ? 'Updating...' : 'Update Token'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// VIEW MODAL
// ─────────────────────────────────────────────────────────────────────────────
const ViewModal = ({ customer: c, onClose, onEdit, onPayments, onFCM }) => {
  const [vtab, setVtab] = useState(0);
  const VTABS = ['Info', 'Documents', 'EMI Summary'];
  const plan = c.emiPlan;

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mBox, maxWidth: 780 }} onClick={e => e.stopPropagation()}>
        <div style={S.mHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {c.profileImage
              ? <img src={c.profileImage} alt="Profile" style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'cover', border: '1px solid #e2e8f0' }} />
              : <div style={{ width: 44, height: 44, borderRadius: 12, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User size={22} color="#2563eb" />
                </div>}
            <div>
              <h2 style={{ ...S.mTitle, marginBottom: 2 }}>{c.fullName}</h2>
              <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{c.customerKey}</div>
            </div>
          </div>
          <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div style={{ padding: '10px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 4, background: '#fafafa', flexShrink: 0 }}>
          {VTABS.map((t, i) => <button key={t} style={S.tab(vtab === i)} onClick={() => setVtab(i)}>{t}</button>)}
        </div>

        <div style={S.mBody}>
          {vtab === 0 && (
            <div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                {c.isEnrolled && <span style={S.badge('#16a34a', '#dcfce7')}>✓ Enrolled</span>}
                {c.isActive !== false && <span style={S.badge('#2563eb', '#dbeafe')}>Active</span>}
                {c.emiPlan && <span style={S.badge('#7c3aed', '#ede9fe')}><IndianRupee size={10} /> EMI Active</span>}
                {c.fcmToken && <span style={S.badge('#ea580c', '#ffedd5')}><Smartphone size={10} /> FCM Set</span>}
              </div>

              <div style={S.section}><User size={13} /> Personal Details</div>
              <div style={S.grid2}>
                {[
                  ['Full Name',       c.fullName       || '—'],
                  ['Phone',           c.phoneNumber    || '—'],
                  ['Alternate Phone', c.alternatePhone || '—'],
                  ['Email',           c.email          || '—'],
                  ['Address',         c.address        || '—'],
                  ['City / State',    `${c.city || '—'} / ${c.state || '—'}`],
                  ['Pincode',         c.pincode        || '—'],
                  ['Aadhar No.',      c.aadharNumber   || '—'],
                  ['PAN No.',         c.panNumber      || '—'],
                  ['IMEI 1',          c.imei1          || '—'],
                  ['IMEI 2',          c.imei2          || '—'],
                  ['Notes',           c.notes          || '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#0f172a', wordBreak: 'break-word' }}>{v}</div>
                  </div>
                ))}
              </div>

              {c.fcmToken && (
                <>
                  <div style={S.divider} />
                  <div style={S.section}><Smartphone size={13} /> FCM Token</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#475569', background: '#f8fafc', padding: '10px 12px', borderRadius: 8, wordBreak: 'break-all', border: '1px solid #e2e8f0' }}>
                    {c.fcmToken}
                  </div>
                </>
              )}
            </div>
          )}

          {vtab === 1 && (
            <div>
              <div style={S.section}><FileText size={13} /> Uploaded Documents</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
                {[['Profile Photo', c.profileImage], ['Aadhar Card', c.aadharCard], ['PAN Card', c.panCard]].map(([label, src]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{label}</div>
                    {src
                      ? <img src={src} alt={label} style={{ width: 110, height: 75, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => window.open(src, '_blank')} />
                      : <div style={{ width: 110, height: 75, borderRadius: 8, border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94a3b8' }}>Not uploaded</div>}
                  </div>
                ))}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Digital Signature</div>
                  {c.digitalSignature
                    ? <img src={c.digitalSignature} alt="Signature" style={{ width: 170, height: 75, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fafafa' }} />
                    : <div style={{ width: 170, height: 75, borderRadius: 8, border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94a3b8' }}>Not signed</div>}
                  {c.signedAt && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{new Date(c.signedAt).toLocaleDateString('en-IN')}</div>}
                </div>
              </div>
            </div>
          )}

          {vtab === 2 && plan && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 18 }}>
                {[
                  ['Product',       plan.productName,            '#0f172a', '#f1f5f9'],
                  ['Product Price', fmt(plan.productPrice),      '#0f172a', '#f1f5f9'],
                  ['EMI Amount',    fmt(plan.emiAmount),         '#2563eb', '#dbeafe'],
                  ['Total Payable', fmt(plan.totalPayable),      '#7c3aed', '#ede9fe'],
                  ['Down Payment',  fmt(plan.downPayment),       '#16a34a', '#dcfce7'],
                  ['Interest',      fmt(plan.totalInterest),     '#dc2626', '#fee2e2'],
                  ['Tenure',        `${plan.tenure} ${plan.tenureType}`, '#ea580c', '#ffedd5'],
                  ['Plan Status',   plan.status,                 '#0f172a', '#f1f5f9'],
                ].map(([k, v, color, bg]) => (
                  <div key={k} style={{ background: bg, borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color }}>{v}</div>
                  </div>
                ))}
              </div>

              {plan.payments?.length > 0 && (() => {
                const next = plan.payments.find(p => p.status !== 'Paid');
                const paid = plan.payments.filter(p => p.status === 'Paid').length;
                const pct  = plan.payments.length ? Math.round((paid / plan.payments.length) * 100) : 0;
                return (
                  <>
                    <div style={S.progBar(pct, '#2563eb').outer}><div style={S.progBar(pct, '#2563eb').inner} /></div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, marginBottom: 14 }}>{paid} of {plan.payments.length} installments paid ({pct}%)</div>
                    <div style={S.section}><Calendar size={13} /> Next Due</div>
                    {next
                      ? <div style={{ ...S.infoBox('#dc2626'), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 700, color: '#0f172a' }}>Installment #{next.installmentNo}</div>
                            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Due: {typeof next.dueDate === 'string' ? next.dueDate : new Date(next.dueDate).toISOString().split('T')[0]}</div>
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626' }}>{fmt(next.emiAmount)}</div>
                        </div>
                      : <div style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>✓ All payments completed!</div>}
                  </>
                );
              })()}
            </div>
          )}

          {vtab === 2 && !plan && (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
              <IndianRupee size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div>No EMI plan configured for this customer.</div>
            </div>
          )}
        </div>

        <div style={S.mFoot}>
          <button style={{ ...S.btnSec, borderColor: '#ea580c', color: '#ea580c' }} onClick={() => { onClose(); onFCM(c); }}>
            <Smartphone size={14} /> FCM Token
          </button>
          {plan?.payments?.length > 0 && (
            <button style={S.btnGrn} onClick={() => { onClose(); onPayments(c); }}>
              <IndianRupee size={14} /> Payments
            </button>
          )}
          <button style={S.btnSec} onClick={() => { onClose(); onEdit(c); }}>
            <Edit2 size={14} /> Edit
          </button>
          <button style={S.btnPri} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMER FORM MODAL  (Create / Edit)
// ─────────────────────────────────────────────────────────────────────────────
const FORM_TABS = ['Personal Info', 'Documents & Sign', 'EMI Plan'];

const emptyForm = () => ({
  fullName: '', phoneNumber: '', alternatePhone: '', email: '',
  address: '', city: '', state: '', pincode: '',
  aadharNumber: '', panNumber: '',
  imei1: '', imei2: '',
  profileImage: null, aadharCard: null, panCard: null,
  digitalSignature: null, signedAt: null,
  notes: '',
  emiPlan: null,
});

const CustomerFormModal = ({ mode, customer, onClose, onSaved }) => {
  const [tab, setTab]       = useState(0);
  const [data, setData]     = useState(() => customer ? { ...emptyForm(), ...customer } : emptyForm());
  const [files, setFiles]   = useState({});
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [emiEnabled, setEmiEnabled] = useState(!!(customer?.emiPlan));

  const set = (field, val) => setData(d => ({ ...d, [field]: val }));

  const inp = (field) => ({
    style: S.input,
    value: data[field] || '',
    onChange: e => set(field, e.target.value),
  });

  const handleFile = (key, dataUrl, file) => {
    set(key, dataUrl);
    setFiles(f => ({ ...f, [key]: file }));
  };

  const validate = () => {
    const errs = [];
    if (!data.fullName?.trim())    errs.push('Full name is required');
    if (!data.phoneNumber?.trim()) errs.push('Phone number is required');
    if (data.phoneNumber && !/^\d{10}$/.test(data.phoneNumber.replace(/\D/g, '')))
      errs.push('Phone number must be 10 digits');
    if (emiEnabled && data.emiPlan) {
      if (!data.emiPlan.productPrice || data.emiPlan.productPrice <= 0)
        errs.push('EMI: Product price is required');
      if (!data.emiPlan.emiAmount || data.emiPlan.emiAmount <= 0)
        errs.push('EMI: Please click "Calculate EMI" to generate the schedule first');
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length) { setErrors(errs); if (errs.some(e => e.startsWith('Full') || e.startsWith('Phone'))) setTab(0); return; }
    setErrors([]);
    setSaving(true);

    try {
      const payload = { ...data, emiPlan: emiEnabled ? data.emiPlan : null };
      const r = mode === 'add'
        ? await customerController.create(payload, files)
        : await customerController.update(customer._id || customer.id, payload, files);

      if (r.success) {
        onSaved(r.data, mode);
      } else {
        const msgs = r.errors?.length ? r.errors : [r.message || 'Something went wrong'];
        setErrors(msgs);
      }
    } catch (e) {
      setErrors([e.message || 'Unexpected error occurred']);
    }
    setSaving(false);
  };

  return (
    <div style={S.modal} onClick={!saving ? onClose : undefined}>
      <div style={{ ...S.mBox, maxWidth: 820 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={S.mHead}>
          <div>
            <h2 style={S.mTitle}>{mode === 'add' ? 'Add New Customer' : `Edit Customer — ${customer?.fullName}`}</h2>
            {data.customerKey && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, fontFamily: 'monospace' }}>
                Key: {data.customerKey}
              </div>
            )}
          </div>
          {!saving && <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>}
        </div>

        {/* Tabs */}
        <div style={{ padding: '10px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 4, background: '#fafafa', flexShrink: 0 }}>
          {FORM_TABS.map((t, i) => (
            <button key={t} style={S.tab(tab === i)} onClick={() => setTab(i)}>{t}</button>
          ))}
        </div>

        <div style={S.mBody}>
          <ErrList errors={errors} />

          {/* ── TAB 0: PERSONAL INFO ── */}
          {tab === 0 && (
            <div>
              <div style={S.section}><User size={13} /> Personal Information</div>
              <div style={S.grid2}>
                <div>
                  <label style={S.label}>Full Name *</label>
                  <input {...inp('fullName')} placeholder="Customer full name" />
                </div>
                <div>
                  <label style={S.label}>Phone Number *</label>
                  <input {...inp('phoneNumber')} placeholder="10-digit mobile" maxLength={10} />
                </div>
                <div>
                  <label style={S.label}>Alternate Phone</label>
                  <input {...inp('alternatePhone')} placeholder="Alternate mobile" maxLength={10} />
                </div>
                <div>
                  <label style={S.label}>Email</label>
                  <input {...inp('email')} type="email" placeholder="email@example.com" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={S.label}>Address</label>
                  <input {...inp('address')} placeholder="Full address" />
                </div>
                <div>
                  <label style={S.label}>City</label>
                  <input {...inp('city')} placeholder="City" />
                </div>
                <div>
                  <label style={S.label}>State</label>
                  <input {...inp('state')} placeholder="State" />
                </div>
                <div>
                  <label style={S.label}>Pincode</label>
                  <input {...inp('pincode')} placeholder="6-digit pincode" maxLength={6} />
                </div>
              </div>

              <div style={S.divider} />
              <div style={S.section}><FileText size={13} /> Document Numbers</div>
              <div style={S.grid2}>
                <div>
                  <label style={S.label}>Aadhar Number</label>
                  <input {...inp('aadharNumber')} placeholder="12-digit Aadhar" maxLength={12} />
                </div>
                <div>
                  <label style={S.label}>PAN Number</label>
                  <input
                    style={{ ...S.input, textTransform: 'uppercase' }}
                    value={data.panNumber || ''}
                    onChange={e => set('panNumber', e.target.value.toUpperCase())}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </div>
              </div>

              <div style={S.divider} />
              <div style={S.section}><Smartphone size={13} /> Device IMEI (Optional — for device linking)</div>
              <div style={S.grid2}>
                <div>
                  <label style={S.label}>IMEI 1 (Primary)</label>
                  <input {...inp('imei1')} placeholder="15-digit IMEI" maxLength={15} />
                </div>
                <div>
                  <label style={S.label}>IMEI 2 (Dual SIM)</label>
                  <input {...inp('imei2')} placeholder="15-digit IMEI" maxLength={15} />
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <label style={S.label}>Notes</label>
                <textarea
                  style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
                  value={data.notes || ''}
                  onChange={e => set('notes', e.target.value)}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          )}

          {/* ── TAB 1: DOCUMENTS & SIGNATURE ── */}
          {tab === 1 && (
            <div>
              <div style={S.section}><FileText size={13} /> Owner Documents</div>
              <div style={S.grid3}>
                <FileField label="Profile Photo"
                  value={data.profileImage}
                  onChange={(v, f) => handleFile('profileImage', v, f)}
                  accept="image/*" />
                <FileField label="Aadhar Card"
                  value={data.aadharCard}
                  onChange={(v, f) => handleFile('aadharCard', v, f)}
                  accept="image/*,application/pdf" />
                <FileField label="PAN Card"
                  value={data.panCard}
                  onChange={(v, f) => handleFile('panCard', v, f)}
                  accept="image/*,application/pdf" />
              </div>

              <div style={S.divider} />
              <div style={S.section}><Pen size={13} /> Digital Signature</div>
              <div style={{ maxWidth: 500 }}>
                <SignaturePad
                  value={data.digitalSignature}
                  onChange={v => {
                    set('digitalSignature', v);
                    if (v) set('signedAt', new Date().toISOString());
                  }}
                />
                {data.signedAt && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                    Signed at: {new Date(data.signedAt).toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 2: EMI PLAN ── */}
          {tab === 2 && (
            <div>
              <div style={S.section}><IndianRupee size={13} /> EMI / Payment Plan</div>

              {/* Toggle */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
                padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={emiEnabled}
                    onChange={e => {
                      setEmiEnabled(e.target.checked);
                      if (!e.target.checked) set('emiPlan', null);
                    }}
                    style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  Enable EMI plan for this customer
                </label>
                {emiEnabled && <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 'auto' }}>✓ EMI Enabled</span>}
              </div>

              {emiEnabled ? (
                <>
                  <div style={{ ...S.infoBox('#2563eb'), fontSize: 13 }}>
                    💡 Fill in the product details then click <strong>"Calculate EMI & Generate Schedule"</strong>. The schedule will be saved with this customer.
                  </div>
                  <EMISection
                    emiPlan={data.emiPlan}
                    onChange={plan => set('emiPlan', plan)}
                  />
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 24px', color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
                  <IndianRupee size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
                  <div style={{ fontSize: 14 }}>Enable the EMI plan above to configure payment schedule</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={S.mFoot}>
          {tab > 0 && (
            <button style={S.btnSec} onClick={() => setTab(t => t - 1)}>
              <ChevronLeft size={14} /> Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {!saving && <button style={S.btnSec} onClick={onClose}>Cancel</button>}
          {tab < FORM_TABS.length - 1 ? (
            <button style={S.btnPri} onClick={() => setTab(t => t + 1)}>
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button style={S.btnPri} onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : mode === 'add' ? 'Create Customer' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const AllCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [sortField, setSortF]     = useState('createdAt');
  const [sortDir,   setSortD]     = useState('desc');
  const [page,      setPage]      = useState(1);
  const PER = 10;

  const [showAdd,  setShowAdd]  = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showDel,  setShowDel]  = useState(false);
  const [showPay,  setShowPay]  = useState(false);
  const [showFCM,  setShowFCM]  = useState(false);
  const [sel,      setSel]      = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Fetch ──
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    const r = await customerController.getAll();
    if (r.success) {
      setCustomers(r.data || []);
    } else {
      showToast('Error loading customers: ' + r.message, 'error');
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // ── Filter + sort ──
  useEffect(() => {
    let r = [...customers];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(c =>
        [c.fullName, c.phoneNumber, c.email, c.customerKey, c.imei1, c.imei2, c.city, c.aadharNumber]
          .some(v => v?.toLowerCase().includes(q))
      );
    }
    r.sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    setFiltered(r);
    setPage(1);
  }, [customers, search, sortField, sortDir]);

  const handleSort = (f) => {
    if (sortField === f) setSortD(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortF(f); setSortD('asc'); }
  };

  // ── Saved callback (create/edit) ──
  const handleSaved = useCallback((savedData, mode) => {
    setShowAdd(false);
    setShowEdit(false);
    fetchCustomers();
    showToast(mode === 'add' ? 'Customer created successfully!' : 'Customer updated successfully!');
  }, [fetchCustomers, showToast]);

  // ── Delete ──
  const handleDelete = async () => {
    const r = await customerController.remove(sel._id || sel.id);
    if (r.success) {
      setShowDel(false);
      setSel(null);
      fetchCustomers();
      showToast('Customer deleted successfully');
    } else {
      showToast(r.message || 'Delete failed', 'error');
    }
  };

  // ── Payment update callback ──
  const handlePaymentUpdate = useCallback((updatedPayments) => {
    setCustomers(prev => prev.map(c => {
      if ((c._id || c.id) === (sel?._id || sel?.id)) {
        return { ...c, emiPlan: { ...c.emiPlan, payments: updatedPayments } };
      }
      return c;
    }));
  }, [sel]);

  // ── FCM updated callback ──
  const handleFCMUpdated = useCallback((newToken) => {
    setCustomers(prev => prev.map(c => {
      if ((c._id || c.id) === (sel?._id || sel?.id)) return { ...c, fcmToken: newToken };
      return c;
    }));
    showToast('FCM token updated successfully!');
  }, [sel, showToast]);

  const paginated  = filtered.slice((page - 1) * PER, page * PER);
  const totalPages = Math.ceil(filtered.length / PER);

  const stats = {
    total:    customers.length,
    enrolled: customers.filter(c => c.isEnrolled).length,
    withEMI:  customers.filter(c => c.emiPlan).length,
    overdue:  customers.filter(c => c.emiPlan?.payments?.some(p => p.status === 'Overdue')).length,
    withFCM:  customers.filter(c => c.fcmToken).length,
  };

  const SortIco = ({ f }) => sortField === f
    ? (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)
    : null;

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? '#dc2626' : '#16a34a',
          color: '#fff', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,.2)', display: 'flex', alignItems: 'center', gap: 8, maxWidth: 400,
        }}>
          {toast.type === 'error' ? <XCircle size={16} /> : <CheckCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', margin: 0 }}>All Customers</h1>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{customers.length} customers registered</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.btnSec} onClick={fetchCustomers}><RefreshCw size={15} /> Refresh</button>
          <button style={S.btnSec}><Download size={15} /> Export</button>
          <button style={S.btnPri} onClick={() => setShowAdd(true)}><Plus size={15} /> Add Customer</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14, marginBottom: 24 }}>
        {[
          ['Total Customers', stats.total,    '#2563eb', <User size={20} color="#2563eb" />],
          ['Enrolled',        stats.enrolled, '#16a34a', <CheckCircle size={20} color="#16a34a" />],
          ['With EMI',        stats.withEMI,  '#7c3aed', <IndianRupee size={20} color="#7c3aed" />],
          ['Overdue EMI',     stats.overdue,  '#dc2626', <AlertTriangle size={20} color="#dc2626" />],
          ['FCM Registered',  stats.withFCM,  '#ea580c', <Smartphone size={20} color="#ea580c" />],
        ].map(([label, val, color, icon]) => (
          <div key={label} style={{ ...S.card, padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>{icon}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            placeholder="Search by name, phone, email, IMEI, key..."
            style={{ ...S.input, paddingLeft: 38 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>Loading customers...</div>
        ) : paginated.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>
            {search ? 'No customers match your search.' : 'No customers yet. Click "Add Customer" to get started.'}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[
                      ['customerKey', 'Key'],
                      ['fullName',    'Customer'],
                      ['phoneNumber', 'Phone'],
                      ['city',        'City'],
                    ].map(([f, l]) => (
                      <th key={f} style={S.th} onClick={() => handleSort(f)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{l}<SortIco f={f} /></div>
                      </th>
                    ))}
                    <th style={S.th}>EMI</th>
                    <th style={S.th}>Device</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(c => {
                    const plan = c.emiPlan;
                    const next = plan?.payments?.find(p => p.status !== 'Paid');
                    const hasOverdue = plan?.payments?.some(p => p.status === 'Overdue');

                    return (
                      <tr key={c._id || c.id || c.customerKey}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                        {/* Key */}
                        <td style={S.td}>
                          <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569', background: '#f1f5f9', padding: '3px 7px', borderRadius: 5, display: 'inline-block' }}>
                            {c.customerKey || '—'}
                          </div>
                        </td>

                        {/* Customer name + profile */}
                        <td style={{ ...S.td, cursor: 'pointer' }} onClick={() => { setSel(c); setShowView(true); }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {c.profileImage
                              ? <img src={c.profileImage} alt="" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                              : <div style={{ width: 34, height: 34, borderRadius: 8, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <User size={16} color="#2563eb" />
                                </div>}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{c.fullName}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.email || c.aadharNumber || ''}</div>
                            </div>
                          </div>
                        </td>

                        {/* Phone */}
                        <td style={S.td}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{c.phoneNumber || '—'}</div>
                          {c.alternatePhone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.alternatePhone}</div>}
                        </td>

                        {/* City */}
                        <td style={S.td}>
                          <div style={{ fontSize: 13 }}>{c.city || '—'}</div>
                          {c.state && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.state}</div>}
                        </td>

                        {/* EMI */}
                        <td style={S.td}>
                          {plan ? (
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>
                                {fmt(plan.emiAmount)}
                                <span style={{ fontWeight: 400, color: '#94a3b8' }}>/{plan.tenureType?.slice(0, 3)}</span>
                              </div>
                              {hasOverdue && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>⚠ Overdue</div>}
                              {!hasOverdue && next && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Due: {typeof next.dueDate === 'string' ? next.dueDate : new Date(next.dueDate).toISOString().split('T')[0]}</div>}
                              {!next && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>✓ Completed</div>}
                            </div>
                          ) : <span style={{ fontSize: 12, color: '#d1d5db' }}>—</span>}
                        </td>

                        {/* Device / IMEI */}
                        <td style={S.td}>
                          {c.imei1
                            ? <div>
                                <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569' }}>{c.imei1}</div>
                                {c.imei2 && <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8' }}>{c.imei2}</div>}
                              </div>
                            : <span style={{ fontSize: 12, color: '#d1d5db' }}>—</span>}
                          {c.isEnrolled && <div style={{ marginTop: 3 }}><span style={S.badge('#16a34a', '#dcfce7')}>Enrolled</span></div>}
                        </td>

                        {/* Status */}
                        <td style={S.td}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={S.badge(c.isActive !== false ? '#16a34a' : '#dc2626', c.isActive !== false ? '#dcfce7' : '#fee2e2')}>
                              {c.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                            {c.fcmToken && <span style={S.badge('#ea580c', '#ffedd5')}><Smartphone size={9} /> FCM</span>}
                          </div>
                        </td>

                        {/* Actions */}
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            <button style={S.iconBtn} title="View" onClick={() => { setSel(c); setShowView(true); }}>
                              <Eye size={15} color="#64748b" />
                            </button>
                            <button style={S.iconBtn} title="Edit" onClick={() => { setSel(c); setShowEdit(true); }}>
                              <Edit2 size={15} color="#2563eb" />
                            </button>
                            <button style={S.iconBtn} title="FCM Token" onClick={() => { setSel(c); setShowFCM(true); }}>
                              <Smartphone size={15} color="#ea580c" />
                            </button>
                            {plan?.payments?.length > 0 && (
                              <button style={S.iconBtn} title="Payments" onClick={() => { setSel(c); setShowPay(true); }}>
                                <IndianRupee size={15} color="#7c3aed" />
                              </button>
                            )}
                            <button style={S.iconBtn} title="Delete" onClick={() => { setSel(c); setShowDel(true); }}>
                              <Trash2 size={15} color="#dc2626" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                Showing {(page - 1) * PER + 1}–{Math.min(page * PER, filtered.length)} of {filtered.length}
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button style={S.btnSec} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
                <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages || 1}</span>
                <button style={S.btnSec} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || totalPages === 0}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── MODALS ── */}

      {/* Create */}
      {showAdd && (
        <CustomerFormModal mode="add" customer={null} onClose={() => setShowAdd(false)} onSaved={handleSaved} />
      )}

      {/* Edit */}
      {showEdit && sel && (
        <CustomerFormModal mode="edit" customer={sel} onClose={() => setShowEdit(false)} onSaved={handleSaved} />
      )}

      {/* View */}
      {showView && sel && (
        <ViewModal
          customer={sel}
          onClose={() => setShowView(false)}
          onEdit={(c) => { setShowView(false); setSel(c); setShowEdit(true); }}
          onPayments={(c) => { setShowView(false); setSel(c); setShowPay(true); }}
          onFCM={(c) => { setShowView(false); setSel(c); setShowFCM(true); }}
        />
      )}

      {/* Payments */}
      {showPay && sel && (
        <PaymentHistoryModal
          customer={sel}
          onClose={() => setShowPay(false)}
          onUpdate={handlePaymentUpdate}
        />
      )}

      {/* FCM Token */}
      {showFCM && sel && (
        <FCMTokenModal
          customer={sel}
          onClose={() => setShowFCM(false)}
          onUpdated={handleFCMUpdated}
        />
      )}

      {/* Delete confirm */}
      {showDel && sel && (
        <div style={S.modal} onClick={() => setShowDel(false)}>
          <div style={{ ...S.mBox, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={S.mHead}>
              <h2 style={S.mTitle}>Delete Customer</h2>
              <button style={S.iconBtn} onClick={() => setShowDel(false)}><X size={20} /></button>
            </div>
            <div style={{ padding: '28px 24px', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Trash2 size={28} color="#dc2626" />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Delete "{sel.fullName}"?</p>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                This will permanently delete the customer and all associated data. This action cannot be undone.
              </p>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', textAlign: 'left', fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{sel.fullName}</div>
                <div style={{ color: '#64748b', marginTop: 2 }}>{sel.phoneNumber} · {sel.email || 'No email'}</div>
                {sel.customerKey && <div style={{ color: '#94a3b8', marginTop: 2, fontFamily: 'monospace', fontSize: 11 }}>{sel.customerKey}</div>}
              </div>
            </div>
            <div style={S.mFoot}>
              <button style={S.btnSec} onClick={() => setShowDel(false)}>Cancel</button>
              <button style={S.btnDng} onClick={handleDelete}>Delete Customer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllCustomersPage;